import { gql, useQuery } from '@apollo/client'
import { ForbiddenError } from '../components/ErrorBoundary'
import { StructParams, StructureBase, getChildType, QueryStructParams, VDCounts } from '../lib/structures'
import type { Timeline } from '../types/Timeline'
import { useEffect } from 'react'

interface RegistrationDataForChildren {
  rollAll: number
  roll7Days: number
  roll30Days: number
  rollWeeklyAvg: number
  rollWeeklyAvgRequired: number
  rollSheet: number
  tags: {
    wrSheet: number
    astregSheet: number
    wrSheetRoll: number
    astregSheetRoll: number
  }
  milestone: number
  target: number
}

interface RegistrationDataForSelf extends RegistrationDataForChildren {
  historicRoll: Timeline<number>
  milestones: Timeline<number>
}

export interface StructureWithRegistration extends StructureWithRegistrationBase {
  registration: RegistrationDataForSelf
  parents: StructureBase[]
  children: StructureWithRegistrationBase[]
}

interface StructureReturns {
  stats: StructureWithRegistration | undefined
  loading: boolean
}

interface StructureWithRegistrationBase extends StructureBase {
  registration: RegistrationDataForChildren
  counts: VDCounts
}

interface RegistrationResult {
  structure: StructureWithRegistration
}

const query = gql`
query getRegistrationData($type: String!, $code: String!, $childType: String!, $filterLabel: String) {
  structure(type: $type, code: $code, filterLabel: $filterLabel) {
    type
    code
    name
    responsiblePersons { firstname surname displayname }
    userHasAccess
    structureHasAssignedWards
    counts {
      total
      filtered
    }
    registration {
        rollAll
        roll7Days
        roll30Days
        rollWeeklyAvg
        rollWeeklyAvgRequired
        milestone
        target
        historicRoll {
          date
          count
        }
        milestones {
          date
          count
        }
      }
    parents { type code name userHasAccess }
    children(type: $childType) {
      type
      code
      name
      responsiblePersons { firstname surname displayname }
      userHasAccess
      structureHasAssignedWards
      counts {
        total
        filtered
      }
      registration {
        rollAll
        roll7Days
        roll30Days
        rollWeeklyAvg
        rollWeeklyAvgRequired
        milestone
        target
      }
    }
  }
}
`

export function useRegistration ({ type, code, filterLabel }: StructParams): StructureReturns {
  const childType = getChildType({ type, code })
  const { error, data, refetch, loading } = useQuery<RegistrationResult, QueryStructParams>(
    query, { variables: { type, code, childType, filterLabel }, notifyOnNetworkStatusChange: true }
  )

  useEffect(() => {
    refetch()
  }, [filterLabel, refetch])

  if (error?.graphQLErrors[0]?.extensions?.code === 'FORBIDDEN') {
    throw new ForbiddenError('this structure')
  }
  if (error != null) throw error

  return { stats: data?.structure, loading: loading }
}
